import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Building2, Loader2, MailX, Mail, CheckCircle2 } from "lucide-react";
import {
  BreadcrumbPage,
  Button,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTableScroll,
  DataTableSelectionSummary,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@eduviaos/ui-kit";
import { TenantsBreadcrumbs } from "@/components/AppBreadcrumbs";
import { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";
import { CreateTenantForm, type CreateTenantInput } from "@/components/CreateTenantForm";
import { SendAdminInviteDialog } from "@/components/SendAdminInviteDialog";
import { TenantActionsCell } from "@/components/TenantActionsCell";

/**
 * Public projection of a tenant as the BE returns it. Aligned with
 * `app.domains.onboarding.schemas.TenantListItem`.
 */
export interface TenantRow {
  slug: string;
  name: string;
  type: "school" | "coaching_institute" | "study_centre";
  status: "active" | "suspended" | "archived";
  created_at: string;
  /** Phase B2.3 — false until the SA explicitly sends the first admin
   *  invite via the deferred-invite flow. Drives the
   *  "Awaiting admin invite" badge + the "Send invite" CTA. */
  has_admin_user?: boolean;
}

interface TenantListResponse {
  items: TenantRow[];
  total: number;
}

interface CreateTenantResponse {
  slug: string;
  name: string;
  type: TenantRow["type"];
  status: TenantRow["status"];
  created_at: string;
}

const TYPE_LABEL: Record<TenantRow["type"], string> = {
  school: "School",
  coaching_institute: "Coaching Institute",
  study_centre: "Study Centre",
};

// Maps a tenant's lifecycle status to the canonical `<StatusPill>`
// status keys defined in ui-kit (Iron Rule #16.3). The mapping is
// 1:1 today but kept as a lookup so a future status (e.g. `pending`
// for KYC) doesn't have to ripple through every consumer.
const STATUS_TO_PILL: Record<TenantRow["status"], "active" | "suspended" | "archived"> = {
  active: "active",
  suspended: "suspended",
  archived: "archived",
};

function formatDate(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

interface LastInviteState {
  slug: string;
  admin_email: string;
  accept_url: string;
  expires_at: string;
  token: string;
  email_sent: boolean;
  email_error: string | null;
}

export function TenantsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // `?create=1` is the redirect target for `/tenants/new` (App.tsx).
  // We open the create sheet as a side-effect of arriving with the
  // flag, then strip it from the URL so a refresh doesn't re-open
  // the sheet behind the user. Using a ref guard so the auto-open
  // only fires once per arrival, even under React 18 strict-mode
  // double-mount.
  const [searchParams, setSearchParams] = useSearchParams();
  const [sheetOpen, setSheetOpen] = React.useState(() => searchParams.get("create") === "1");
  const autoOpenedRef = React.useRef(searchParams.get("create") === "1");
  React.useEffect(() => {
    if (autoOpenedRef.current && searchParams.get("create") === "1") {
      const next = new URLSearchParams(searchParams);
      next.delete("create");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  // `lastInvite` only ever holds an invite from a Resend action now —
  // tenant creation is invite-less in the deferred-invite flow (the SA
  // sends the invite later from the tenant detail page).
  const [lastInvite, setLastInvite] = React.useState<LastInviteState | null>(null);
  // SA can also fire the first admin invite directly from the list row
  // for tenants that were created with deferred-invite and don't yet
  // have an admin. This dialog handles that flow.
  const [inviteForTenant, setInviteForTenant] = React.useState<{
    slug: string;
    name: string;
  } | null>(null);

  const tenantsQuery = useQuery({
    queryKey: ["super-admin", "tenants"],
    queryFn: () => api.get<TenantListResponse>("/super-admin/tenants"),
  });

  // Phase B2.3 — Step 1 of the deferred-invite flow. Creates the tenant
  // record only (no admin user, no invite, no email). On success the
  // sheet closes and the SA lands on the tenant detail page where they
  // configure modules / forms / branding before firing the invite.
  const createMutation = useMutation<CreateTenantResponse, Error, CreateTenantInput>({
    mutationFn: (input) => api.post<CreateTenantResponse>("/super-admin/tenants", input),
    onSuccess: (data) => {
      // Phase E1c — seed the list cache with the new row BEFORE navigating.
      // Previously we just `invalidateQueries` and `navigate`d immediately,
      // which created a flash-404 race: TenantDetailPage hydrates `tenant`
      // from the same `["super-admin","tenants"]` cache, and the cache
      // still held the pre-creation list while the invalidation was in
      // flight. The `find(t.slug === newSlug)` returned `undefined`, the
      // guard fell to "Tenant not found", and the page snapped back to
      // the detail view ~300 ms later when the refetch landed. Seeding
      // the row first means the detail page renders cleanly on first
      // mount with the data we already have from the POST response.
      queryClient.setQueryData<TenantListResponse>(["super-admin", "tenants"], (prev) => {
        const seeded: TenantRow = { ...data, has_admin_user: false };
        if (!prev) return { items: [seeded], total: 1 };
        // De-dupe defensively in case the BE somehow already echoed it.
        const filtered = prev.items.filter((t) => t.slug !== seeded.slug);
        return { items: [seeded, ...filtered], total: filtered.length + 1 };
      });
      queryClient.invalidateQueries({ queryKey: ["super-admin", "tenants"] });
      setSheetOpen(false);
      // Land on the detail page so the next step (configure → invite)
      // is one click away.
      navigate(`/tenants/${data.slug}`);
    },
  });

  return (
    // Flex chain so the DataTable below can fill the remaining
    // viewport height and become the ONLY scroll surface (the page
    // itself never scrolls). AppShell's <main> is overflow-y-auto +
    // min-h-0, this `h-full flex flex-col` claims its full height,
    // PageHeader takes natural size, DataTable is flex-1 min-h-0.
    //
    // PageHeader is non-sticky here: in a fixed-frame layout the page
    // doesn't scroll, so sticky-positioning the header has no purpose
    // and worse, in a flex parent it overlaps the DataTable beneath
    // it (the sticky element's natural slot height conflicts with the
    // `-mt-8` negative margin). `sticky={false}` makes it a clean
    // normal-flow block.
    <div className="flex h-full flex-col gap-6">
      <PageHeader sticky={false}>
        <PageHeaderTop>
          <TenantsBreadcrumbs>
            <BreadcrumbPage>Tenants</BreadcrumbPage>
          </TenantsBreadcrumbs>
          <PageHeaderActions>
            <Sheet
              open={sheetOpen}
              onOpenChange={(open) => {
                setSheetOpen(open);
                if (!open) {
                  // Closing the sheet wipes one-shot state so a second
                  // invite doesn't show a stale token.
                  createMutation.reset();
                  setLastInvite(null);
                }
              }}
            >
              <SheetTrigger asChild>
                <Button>
                  <Plus className="size-4" aria-hidden="true" />
                  Create new tenant
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
                <SheetHeader className="border-border/60 shrink-0 border-b px-6 py-4">
                  <SheetTitle>{lastInvite ? "Invite ready" : "Create a new tenant"}</SheetTitle>
                  <SheetDescription>
                    {lastInvite
                      ? "Token shown ONCE — copy the link before closing."
                      : "Creates the tenant only. You'll configure modules, forms, and branding before sending the admin invite."}
                  </SheetDescription>
                </SheetHeader>
                {lastInvite ? (
                  <div className="flex-1 overflow-y-auto px-6 py-6">
                    <InviteSuccessPanel invite={lastInvite} onDone={() => setSheetOpen(false)} />
                  </div>
                ) : (
                  // The form owns its own scroll + sticky footer so the
                  // "Create tenant" button stays in view regardless of
                  // viewport height.
                  <CreateTenantForm
                    onSubmit={(input) => createMutation.mutate(input)}
                    isPending={createMutation.isPending}
                    error={createMutation.error instanceof ApiError ? createMutation.error : null}
                  />
                )}
              </SheetContent>
            </Sheet>
          </PageHeaderActions>
        </PageHeaderTop>
        <PageHeaderHeading>
          <PageHeaderTitle>Tenants</PageHeaderTitle>
          <PageHeaderDescription>
            Every institution running on EduViaOS. Invite new tenants and watch them go from invited
            to onboarded to active.
          </PageHeaderDescription>
        </PageHeaderHeading>
      </PageHeader>

      <DataTable className="flex-1">
        {/*
          Phase B2.5 / Iron Rule #16: the toolbar holds the table's
          own captioning + meta (count, in-flight indicator). The
          page-level title ("Tenants") lives in <PageHeader>; this
          row gives the table its own identity ("All tenants")
          plus the row count without doubling vertical space the
          way a separate Card header used to.
         */}
        <DataTableToolbar>
          <div className="flex flex-col">
            <span className="text-foreground text-sm font-medium">All tenants</span>
            <span className="text-muted-foreground text-xs tabular-nums">
              {tenantsQuery.data?.total ?? 0} institution(s) on the platform
            </span>
          </div>
          <DataTableToolbarSpacer />
          {tenantsQuery.isFetching && (
            <Loader2 className="text-muted-foreground size-4 animate-spin" aria-hidden="true" />
          )}
        </DataTableToolbar>

        <DataTableScroll>
          {/*
            Iron Rule #16.6: a <caption> is mandatory for screen
            readers. The PageHeader title already names the page, so
            we hide the caption visually but keep it in the a11y
            tree.
           */}
          <Table>
            <caption className="sr-only">
              List of every institution on the EduViaOS platform.
            </caption>
            <TableHeader>
              <TableRow>
                <TableHead>Tenant</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[1%] text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            {tenantsQuery.isLoading ? (
              <DataTableSkeleton rows={6} columns={5} />
            ) : tenantsQuery.isError ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={5} className="py-12 text-center">
                    <span className="text-destructive text-sm">
                      {(tenantsQuery.error as Error).message ?? "Failed to load tenants."}
                    </span>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (tenantsQuery.data?.items.length ?? 0) === 0 ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={5} className="p-0">
                    <DataTableEmpty
                      icon={<Building2 className="size-8" aria-hidden="true" />}
                      title="No tenants yet"
                      description="Onboard the first institution by creating a tenant and sending an invite to its admin."
                      action={
                        <Button onClick={() => setSheetOpen(true)}>
                          <Plus className="size-4" aria-hidden="true" />
                          Create the first tenant
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {tenantsQuery.data!.items.map((t) => (
                  <TableRow key={t.slug}>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <span className="bg-primary/10 text-primary grid size-7 shrink-0 place-items-center rounded-md">
                          <Building2 className="size-3.5" aria-hidden="true" />
                        </span>
                        <div className="min-w-0">
                          <Link
                            to={`/tenants/${t.slug}`}
                            className="hover:text-primary truncate font-medium hover:underline"
                          >
                            {t.name}
                          </Link>
                          <p className="text-muted-foreground truncate text-xs">{t.slug}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{TYPE_LABEL[t.type]}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <StatusPill status={STATUS_TO_PILL[t.status]} />
                        {t.has_admin_user === false && (
                          // Phase B2.3 — surface the deferred-invite
                          // state so SAs notice tenants that were
                          // created but never invited. The Tenant
                          // detail page also shows a prominent CTA.
                          <StatusPill
                            status="no-admin"
                            icon={<MailX className="size-3" aria-hidden="true" />}
                          />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground tabular-nums">
                      {formatDate(t.created_at)}
                    </TableCell>
                    <TableCell className="w-[1%] py-2 pr-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {t.has_admin_user === false && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setInviteForTenant({ slug: t.slug, name: t.name })}
                          >
                            <Mail className="size-3.5" aria-hidden="true" />
                            Send invite
                          </Button>
                        )}
                        <TenantActionsCell
                          tenantSlug={t.slug}
                          tenantName={t.name}
                          onResendSuccess={(invite) => {
                            // Surface the new one-shot URL via the same
                            // Sheet the create flow uses — the SA already
                            // knows that visual idiom.
                            setLastInvite(invite);
                            setSheetOpen(true);
                          }}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {/*
          Iron Rule #16.1: footer is rendered even when nothing is
          selected so the table chrome height never jitters on first
          select. Pagination is a no-op for now (single page);
          B2.5b adds real server-side paging.
         */}
        <DataTableFooter>
          <DataTableSelectionSummary selected={0} total={tenantsQuery.data?.items.length ?? 0} />
        </DataTableFooter>
      </DataTable>

      {/* Phase B2.3 — first-admin invite dialog (deferred-invite flow) */}
      {inviteForTenant && (
        <SendAdminInviteDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setInviteForTenant(null);
          }}
          tenantSlug={inviteForTenant.slug}
          tenantName={inviteForTenant.name}
        />
      )}
    </div>
  );
}

function InviteSuccessPanel({ invite, onDone }: { invite: LastInviteState; onDone: () => void }) {
  const [copied, setCopied] = React.useState(false);
  const expiresIn = humaniseExpiry(invite.expires_at);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(invite.accept_url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // Best-effort; some sandboxed environments block clipboard. The
      // textarea below stays selectable as the fallback.
    }
  };

  return (
    <div className="grid gap-4">
      {invite.email_sent ? (
        <div className="flex items-start gap-2.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-200">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-semibold">
              Invite email sent
              {invite.admin_email ? ` to ${invite.admin_email}` : ""}.
            </p>
            <p className="opacity-90">
              Should arrive within a few minutes. Resend from the tenant row if it doesn&apos;t.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200">
          <MailX className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-semibold">Email delivery didn&apos;t complete.</p>
            <p className="opacity-90">
              {invite.email_error ?? "Provider didn't confirm delivery."} The link below still works
              — copy it and forward manually, then hit Resend once email is healthy again.
            </p>
          </div>
        </div>
      )}
      <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/30 dark:bg-amber-950/30 dark:text-amber-200">
        <strong className="font-semibold">Token shown only once.</strong> Closing this sheet wipes
        it from memory.
      </div>
      <div className="grid gap-1.5">
        <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">Tenant</p>
        <p className="font-medium">{invite.slug}</p>
      </div>
      <div className="grid gap-1.5">
        <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
          Invite link
        </p>
        <textarea
          readOnly
          value={invite.accept_url}
          className="bg-muted/40 min-h-[5.5rem] rounded-md border px-3 py-2 font-mono text-xs"
          onFocus={(e) => e.currentTarget.select()}
        />
        <p className="text-muted-foreground text-xs">Expires in {expiresIn}.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={onCopy} variant="default">
          {copied ? "Copied!" : "Copy link"}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}

function humaniseExpiry(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return "less than a minute";
  const hours = Math.round(ms / 3_600_000);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"}`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"}`;
}

// rewriteAcceptUrlForPreview was used by the legacy combined-invite
// create flow; with the deferred-invite flow tenant creation no longer
// returns an invite URL inline (TenantActionsCell handles its own URL
// from the resend response). Helper removed.
